import React from 'react';
import { fromPairs, keyBy, sumBy, sum, isEmpty, chunk, orderBy, groupBy } from 'lodash';
import { doc, collection, query, where, } from 'firebase/firestore';
import { format as formatDate, differenceInCalendarDays, } from 'date-fns';
import { httpsCallable } from 'firebase/functions';

import { functions, getCollectionData, } from '../firebase';
import { mapKeysToJa } from '../shared/texts';
import { taxRates } from '../shared/config';
import { excludeTax, includeTax } from '../shared/util';
import { resolveUnitCostAt, resolveLatestUnitCost, netQuantityOf, shippingQuantityOf } from '../shared/unitCostHistory';
import ExportButton from './ExportButton';

const getProductKpis = httpsCallable(functions, 'getProductKpis');

// エクスポート行作成
const createExportRow = (product, kpis, sellersById) => mapKeysToJa({
  asin: product.amazonAttributes?.asin || '',
  sku: product.sku || '',
  sellerName: sellersById[product.sellerId]?.name || '',
  productName: product.name || '',
  grossSales: kpis.grossSales,
  refund: kpis.refund,
  vineSales: kpis.vineSales,
  sales: kpis.sales,
  grossQuantity: kpis.grossQuantity,
  refundQuantity: kpis.refundQuantity,
  vineQuantity: kpis.vineQuantity,
  quantity: kpis.quantity,
  cost: kpis.cost,
  salesFees: kpis.salesFees,
  systemUsageFee: kpis.systemUsageFee,
  safetyFee: kpis.safetyFee,
  paymentFee: kpis.paymentFee,
  pointsBaseCost: kpis.pointsBaseCost,
  fbaShippingCost: kpis.fbaShippingCost,
  shippingCost: kpis.shippingCost,
  grossProfit: kpis.grossProfit,
  grossProfitRate: kpis.grossProfitRate,
  adCost: kpis.adCost,
  promotionCost: kpis.promotionCost,
  pointsCost: kpis.pointsCost,
  contributionMargin: kpis.contributionMargin,
  contributionMarginRate: kpis.contributionMarginRate,
  adSales: kpis.adSales,
  adSalesRatio: kpis.adSalesRatio,
  organicSales: kpis.organicSales,
  organicSalesRatio: kpis.organicSalesRatio,
  acos: kpis.acos,
  acots: kpis.acots,
  fbaQuantity: kpis.fbaQuantity,
  fbaInventoryAmount: kpis.fbaInventoryAmount,
  dailyAverageQuantity: kpis.dailyAverageQuantity,
  daysUntilStockout: kpis.daysUntilStockout,
});

// 子商品のKPI計算
const calculateChildKpis = (childProduct, salesData, adData, days, isExcludeTax = false) => {
  const productSalesData = salesData.filter(_ => _.productId === childProduct.id);
  const productAdData = adData.filter(_ => _.productId === childProduct.id);

  const handleTax = isExcludeTax ? excludeTax : _ => _;
  const handleTaxExcludedValue = isExcludeTax ? _ => _ : includeTax;

  const sales = handleTax(sumBy(productSalesData, _ => _.values?.sales || 0));
  const refund = handleTax(sumBy(productSalesData, _ => _.values?.refund || 0));
  const quantity = sumBy(productSalesData, _ => _.values?.quantity || 0);
  const refundQuantity = sumBy(productSalesData, _ => _.values?.refundQuantity || 0);
  const vineQuantity = sumBy(productSalesData, _ => _.values?.vineQuantity || 0);
  const vineSales = handleTax(sumBy(productSalesData, _ => _.values?.vineSales || 0));
  const salesFees = handleTax(sumBy(productSalesData, _ => _.values?.commission || 0));
  const systemUsageFee = handleTax(sumBy(productSalesData, _ => _.values?.systemUsageFee || 0));
  const safetyFee = handleTax(sumBy(productSalesData, _ => _.values?.safetyFee || 0));
  const paymentFee = handleTax(sumBy(productSalesData, _ => _.values?.paymentFee || 0));
  const fbaFees = handleTax(sumBy(productSalesData, _ => _.values?.fbaFee || 0));
  const pointsBaseCost = handleTax(sumBy(productSalesData, _ => _.values?.pointsBaseCost || 0));
  const pointsCost = handleTax(sumBy(productSalesData, _ => _.values?.pointsCost || 0));
  const promotionCost = handleTax(sumBy(productSalesData, _ => _.values?.promotionCost || 0));
  const adCost = handleTaxExcludedValue(sumBy(productAdData, _ => _.kpis?.cost || 0));
  const adSales = handleTax(sumBy(productAdData, _ => _.kpis?.sales1d || 0));

  // 原価計算: 売上docの日付ごとに有効原価(unitCostHistory)を引いて正味数量(返品・Vine控除後)を掛ける
  const costTaxRate = taxRates[childProduct.costTaxRate]?.value;
  const cost = sumBy(productSalesData, doc => {
    const raw = resolveUnitCostAt(childProduct, doc.date);
    const dailyUnitCost = isExcludeTax ? excludeTax(raw, costTaxRate) : raw;
    return dailyUnitCost * netQuantityOf(doc.values);
  });
  const unitCost = isExcludeTax
    ? excludeTax(resolveLatestUnitCost(childProduct), costTaxRate)
    : resolveLatestUnitCost(childProduct);

  // 配送料計算
  const unitShippingCost = isExcludeTax ? excludeTax(childProduct.unitShippingCost || 0) : (childProduct.unitShippingCost || 0);
  const fbaShippingCost = Math.abs(fbaFees);
  const shippingCost = unitShippingCost * sumBy(productSalesData, _ => shippingQuantityOf(_.values));

  const netSales = sales + refund - vineSales;
  // ポイント原資(絶対負担1%)は原価2扱いなので純粗利益から控除。pointsBaseCostは負値
  const grossProfit = netSales - cost + salesFees + systemUsageFee + safetyFee + paymentFee - fbaShippingCost - shippingCost + pointsBaseCost;
  const organicSales = netSales - adSales;

  return {
    grossSales: sales,
    refund: Math.abs(refund),
    vineSales,
    sales: sales + refund - vineSales,
    grossQuantity: quantity,
    refundQuantity,
    vineQuantity,
    quantity: quantity - refundQuantity - vineQuantity,
    cost,
    salesFees: -salesFees,
    systemUsageFee: -systemUsageFee,
    safetyFee: -safetyFee,
    paymentFee: -paymentFee,
    pointsBaseCost: -pointsBaseCost,
    fbaShippingCost,
    shippingCost,
    grossProfit,
    grossProfitRate: netSales > 0 ? grossProfit / netSales : 0,
    adCost,
    pointsCost: -pointsCost,
    promotionCost: -promotionCost,
    contributionMargin: grossProfit - adCost + pointsCost + promotionCost,
    contributionMarginRate: netSales > 0 ? (grossProfit - adCost + pointsCost + promotionCost) / netSales : 0,
    adSales,
    adSalesRatio: netSales > 0 ? adSales / netSales : 0,
    organicSales,
    organicSalesRatio: netSales > 0 ? organicSales / netSales : 0,
    acos: adSales > 0 ? adCost / adSales : 0,
    acots: netSales > 0 ? adCost / netSales : 0,
    fbaQuantity: childProduct.fbaQuantity || 0,
    fbaInventoryAmount: (childProduct.fbaQuantity || 0) * unitCost,
    dailyAverageQuantity: days > 0 ? (quantity - refundQuantity - vineQuantity) / days : 0,
    daysUntilStockout: (days > 0 && (quantity - refundQuantity - vineQuantity) > 0)
      ? (childProduct.fbaQuantity || 0) / ((quantity - refundQuantity - vineQuantity) / days)
      : null,
  };
};

// 全商品エクスポート（親商品と子商品）
const getAllProductsForExport = async ({ tenant, startDate, endDate, sortBy, sortOrder, keyword, onlyNoPurchases, tagIds, sellersById, isExcludeTax, onProgress }) => {
  const days = differenceInCalendarDays(endDate, startDate) + 1;
  const fetchProducts = async (currentPage = 1, accumulatedProducts = []) => {
    const response = await getProductKpis({
      tenantId: tenant.id,
      startDate: formatDate(startDate, 'yyyy-MM-dd'),
      endDate: formatDate(endDate, 'yyyy-MM-dd'),
      sortBy,
      sortOrder,
      page: currentPage,
      limit: 1000,
      keyword: !isEmpty(keyword) ? keyword : undefined,
      onlyNoPurchases,
      tagIds,
      isExcludeTax,
    });

    const { products: pageProducts = [], pagination: paginationInfo = {} } = response.data;
    const allProducts = accumulatedProducts.concat(pageProducts);
    onProgress?.(currentPage, paginationInfo.totalPages || 1);

    return paginationInfo.hasNextPage
      ? fetchProducts(currentPage + 1, allProducts)
      : allProducts;
  };

  const allProducts = await fetchProducts();

  const exportRowsArrays = await Promise.all(
    allProducts.map(async product => {
      const parentRow = createExportRow(product, {
        ...product.kpis,
        pointsCost: -product.kpis.pointsCost,
        promotionCost: -product.kpis.promotionCost,
      }, sellersById);

      const hasChildProducts = product.amazonAttributes?.familySkus?.length > 1;
      if (!hasChildProducts) return [parentRow];

      const childProducts = await getCollectionData(
        query(
          collection(tenant.ref, 'products'),
          where('amazonAttributes.parentSku', '==', product.sku),
          where('amazonAttributes.isParent', '==', false)
        )
      );

      const [salesDataArrays, adDataArrays] = await Promise.all([
        Promise.all(chunk(childProducts.map(_ => _.id), 10).map(_ =>
          getCollectionData(query(
            collection(tenant.ref, 'amazonProductSales'),
            where('productId', 'in', _),
            where('date', '>=', formatDate(startDate, 'yyyy-MM-dd')),
            where('date', '<=', formatDate(endDate, 'yyyy-MM-dd'))
          ))
        )),
        Promise.all(chunk(childProducts.map(_ => _.id), 10).map(_ =>
          getCollectionData(query(
            collection(tenant.ref, 'amazonProductAdKpis'),
            where('productId', 'in', _),
            where('date', '>=', formatDate(startDate, 'yyyy-MM-dd')),
            where('date', '<=', formatDate(endDate, 'yyyy-MM-dd'))
          ))
        ))
      ]);

      const salesData = salesDataArrays.flat();
      const adData = adDataArrays.flat();

      const childRows = childProducts.map(childProduct =>
        createExportRow(childProduct, calculateChildKpis(childProduct, salesData, adData, days, isExcludeTax), sellersById)
      );

      return [parentRow, ...childRows];
    })
  );

  return exportRowsArrays.flat();
};

export default function ProductKpiExportButton({ tenant, startDate, endDate, sortBy, sortOrder, keyword, onlyNoPurchases, tagIds, sellersById, isExcludeTax }) {
  const exportRows = onProgress => getAllProductsForExport({
    tenant,
    startDate,
    endDate,
    sortBy,
    sortOrder,
    keyword,
    onlyNoPurchases,
    tagIds,
    sellersById,
    isExcludeTax,
    onProgress,
  });

  return (
    <ExportButton
      label={false}
      outline
      fileName={`商品損益_${formatDate(startDate, 'yyyy-MM-dd')}_${formatDate(endDate, 'yyyy-MM-dd')}.csv`}
      rows={exportRows}
    />
  );
}
